"use client";

import {
    type BrokingChargeBreakdownProps,
    type BrokingChargeData,
    type BrokingChargeRow,
    type BrokingChargeTab,
    type BrokingChargeTabId,
} from "@/app/interface/broking";
import { cn } from "@/lib/utils";
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";

const TABS: BrokingChargeTab[] = [
    { id: "equity", label: "Equity" },
    { id: "fno", label: "F&O" },
    { id: "commodity", label: "Commodity" },
];

const CHARGE_DATA: BrokingChargeData = {
    equity: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            {
                charge: "Brokerage",
                values: ["0.1% or ₹9/order (whichever is lower)", "0.01% or ₹9/order (lower)"],
            },
            {
                charge: "STT",
                values: ["0.1% on buy & sell", "0.025% on sell side"],
            },
            {
                charge: "Transaction Charges",
                values: ["₹307/crore", "₹307/crore"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            {
                charge: "SEBI Charges",
                values: ["₹10/crore", "₹10/crore"],
            },
            {
                charge: "Stamp Charges",
                values: ["0.015% on buy side", "0.003% on buy side"],
            },
            {
                charge: "DP Charges (Sell)",
                values: ["₹15 + 18% GST per script", "—"],
            },
        ],
    },
    fno: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            { charge: "Brokerage", values: ["₹9/Order", "₹9/Order"] },
            {
                charge: "STT",
                values: ["0.05% on Sell Side", "0.15% on Sell Side (on Premium), 0.15% to buy"],
            },
            {
                charge: "Transaction Charges",
                values: ["₹225/crore", "₹4003/crore of premium"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            { charge: "SEBI Charges", values: ["₹10/crore", "₹10/Crore"] },
            {
                charge: "Stamp Charges",
                values: ["0.002% on Buy-side", "0.003% on Buy-side"],
            },
        ],
    },
    commodity: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            { charge: "Brokerage", values: ["₹9/Order", "₹9/Order"] },
            {
                charge: "STT",
                values: [
                    "0.01% on Sell Side (Non-Agri)",
                    "0.05% on Sell Side (on Premium), 0.125% to buy",
                ],
            },
            {
                charge: "Transaction Charges",
                values: ["₹310/crore (Non-Agri)", "₹5180/crore of premium"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            { charge: "SEBI Charges", values: ["₹10/crore", "₹10/Crore"] },
            {
                charge: "Stamp Charges",
                values: ["0.002% on Buy-side", "0.003% on Buy-side"],
            },
        ],
    },
};

const SCROLL_TRACK_WIDTH = 180;

interface TabButtonProps {
    tab: BrokingChargeTab;
    isActive: boolean;
    onClick: (id: BrokingChargeTabId) => void;
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onClick(tab.id)}
            className={cn(
                "relative flex h-8 shrink-0 items-center justify-center whitespace-nowrap px-2 py-2 text-base font-medium leading-6 transition-colors md:px-3 md:text-lg",
                isActive ? "bg-white text-brand-primary-500" : "text-[#767676] hover:text-neutral-700",
            )}
        >
            {tab.id === "fno" ? (
                <>
                    <span className="md:hidden">F&O</span>
                    <span className="hidden md:inline">F&O</span>
                </>
            ) : (
                tab.label
            )}
            <span
                aria-hidden
                className={cn(
                    "absolute inset-x-0 bottom-0 h-0.5",
                    isActive ? "bg-brand-primary-500" : "bg-transparent",
                )}
            />
        </button>
    );
}

interface TableRowProps {
    row: BrokingChargeRow;
    isLast: boolean;
}

function ChargeTableRow({ row, isLast }: TableRowProps) {
    return (
        <tr className={cn("h-[50px] md:h-auto", !isLast && "border-b border-[#E6E6E6]")}>
            <td className="w-[188px] min-w-[188px] whitespace-nowrap px-4 align-middle text-left text-sm font-medium leading-5 text-[#292929] md:w-auto md:min-w-[200px] md:py-3.5 md:text-base md:leading-[22px]">
                {row.charge}
            </td>
            {row.values.map((value, index) => (
                <td
                    key={`${row.charge}-${index}`}
                    className={cn(
                        "min-w-[273px] whitespace-nowrap px-4 align-middle text-left text-sm font-medium leading-5 text-[#292929] md:min-w-0 md:whitespace-normal md:py-3.5 md:text-base md:leading-[22px]",
                        value === "—" && "text-neutral-400",
                    )}
                >
                    {value}
                </td>
            ))}
        </tr>
    );
}

function ChargeTableScroll({ children }: { children: ReactNode }) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState({ width: 40, left: 0, canScroll: false });

    const updateThumb = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }

        if (el.scrollLeft < 0) {
            el.scrollLeft = 0;
        }

        const { scrollLeft, scrollWidth, clientWidth } = el;
        const canScroll = scrollWidth - clientWidth > 1;
        const width = canScroll
            ? Math.max(40, (clientWidth / scrollWidth) * SCROLL_TRACK_WIDTH)
            : SCROLL_TRACK_WIDTH;
        const maxScroll = scrollWidth - clientWidth;
        const left = maxScroll > 0 ? (scrollLeft / maxScroll) * (SCROLL_TRACK_WIDTH - width) : 0;

        setThumb({ width, left, canScroll });
    }, []);

    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }

        el.scrollLeft = 0;
        updateThumb();
        const observer = new ResizeObserver(updateThumb);
        observer.observe(el);
        if (el.firstElementChild) {
            observer.observe(el.firstElementChild);
        }

        return () => observer.disconnect();
    }, [updateThumb, children]);

    return (
        <div className="flex w-full min-w-0 flex-col gap-6">
            <div
                ref={scrollerRef}
                dir="ltr"
                onScroll={updateThumb}
                className="w-full min-w-0 max-w-full touch-pan-x overflow-x-auto overscroll-x-contain rounded-2xl border border-[#E6E6E6] scrollbar-hide [-webkit-overflow-scrolling:touch] md:[scrollbar-width:thin] md:[scrollbar-color:#D4D4D4_transparent] md:[&::-webkit-scrollbar]:h-1 md:[&::-webkit-scrollbar-track]:bg-transparent md:[&::-webkit-scrollbar-thumb]:rounded-full md:[&::-webkit-scrollbar-thumb]:bg-[#D4D4D4]"
            >
                {children}
            </div>
            <div
                aria-hidden
                className={cn(
                    "flex w-full justify-center md:hidden",
                    !thumb.canScroll && "hidden",
                )}
            >
                <div className="relative h-1.5 w-[180px] overflow-hidden rounded">
                    <div className="absolute inset-0 rounded bg-[#E6E6E6]" />
                    <div
                        className="absolute top-0 h-1.5 rounded bg-[#B3B3B3]"
                        style={{ width: thumb.width, transform: `translateX(${thumb.left}px)` }}
                    />
                </div>
            </div>
        </div>
    );
}

export default function BrokingChargeBreakdown({
    data = CHARGE_DATA,
    tabs = TABS,
    title = "Detailed Charge Breakdown",
}: BrokingChargeBreakdownProps) {
    const [activeTab, setActiveTab] = useState<BrokingChargeTabId>(tabs[0]?.id ?? "equity");

    const current = data[activeTab];

    if (!current) {
        return null;
    }

    return (
        <div className="w-full min-w-0 max-w-full overflow-hidden">
            <div
                role="tablist"
                aria-label="Charge segments"
                className="relative z-10 flex w-max max-w-full min-w-0 items-center overflow-x-auto rounded-t-[10px] bg-white p-2 scrollbar-hide md:p-2.5"
            >
                <div className="flex items-center gap-2 md:gap-4">
                    {tabs.map((tab) => (
                        <TabButton
                            key={tab.id}
                            tab={tab}
                            isActive={activeTab === tab.id}
                            onClick={setActiveTab}
                        />
                    ))}
                </div>
            </div>

            <div className="relative z-0 w-full min-w-0 max-w-full overflow-hidden rounded-2xl rounded-tl-none bg-white">
                <div className="flex min-w-0 flex-col gap-6 px-4 py-8 md:p-8">
                    <h3 className="text-xl font-bold leading-[26px] text-black">
                        {title}
                    </h3>

                    <ChargeTableScroll key={activeTab}>
                        <table className="w-max min-w-[734px] border-collapse md:w-full md:min-w-[520px]">
                            <thead>
                                <tr className="h-[50px] bg-[#E6E6E6] md:h-auto">
                                    <th className="w-[188px] min-w-[188px] whitespace-nowrap px-4 text-left text-xs font-semibold leading-4 text-black md:w-auto md:min-w-[200px] md:py-3.5 md:text-base md:leading-[22px]">
                                        Charge
                                    </th>
                                    {current.columns.map((column) => (
                                        <th
                                            key={column}
                                            className="min-w-[273px] whitespace-nowrap px-4 text-left text-xs font-semibold leading-4 text-black md:min-w-0 md:py-3.5 md:text-base md:leading-[22px]"
                                        >
                                            {column}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {current.rows.map((row, index) => (
                                    <ChargeTableRow
                                        key={row.charge}
                                        row={row}
                                        isLast={index === current.rows.length - 1}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </ChargeTableScroll>
                </div>
            </div>
        </div>
    );
}
